/**
 * Ventana «Lo vendido» del inicio.
 *
 * Es lo que pidió el dueño, textual: «quiero que se vea un lugar donde se vean
 * todas las ventas y el total facturado del mes, es decir el total de las ventas
 * que se vienen llevando a cabo». Dos cosas, y las dos están acá: el número, y
 * la puerta a la lista que lo compone.
 *
 * TRES DECISIONES QUE HACEN QUE EL NÚMERO NO MIENTA
 *
 * 1. **La suma la hace el servidor, no esta pantalla.** Cada tarjeta pide el
 *    listado con `limit: 1` y se queda con `count` y `totals`: el total viene
 *    calculado sobre el filtro entero, igual que el pie de la pantalla de
 *    Ventas —de hecho es exactamente el mismo mecanismo, `ListView.totals`—. Si
 *    se sumara acá habría que traerse las 380 filas para mostrar un número, y
 *    peor todavía: sumar importes en JavaScript mete un float en el camino de la
 *    plata. Los importes viajan como string justamente para evitar eso.
 *
 * 2. **Se corta por «Vendido el» (`start_date`), no por la fecha de la
 *    factura.** El dueño preguntó por «las ventas que se vienen llevando a
 *    cabo», y una venta existe desde que se cerró, no desde que se facturó
 *    —facturar es un acto aparte y deliberado en Núcleo, nunca automático—. Con
 *    el otro criterio, el mes arrancaría en cero hasta que alguien emitiera algo
 *    y el vendedor no vería su propio trabajo.
 *
 * 3. **Los meses se calculan por año y mes, no sumándole 30 días a hoy.** El
 *    primer día del mes que viene es `new Date(año, mes + 1, 1)` y el
 *    constructor normaliza diciembre solo; restar días a mano se rompe en
 *    febrero y en los cambios de año.
 *
 * Si el usuario no puede leer las ventas —un operador de consorcios, por
 * ejemplo— el panel no se dibuja, en silencio. Mismo criterio que
 * `ProximosPanel`.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { formatCurrency } from '@/components/fields/format';
import { IconChevronRight } from '@/components/shell/icons';
import { filteredModelPath, modelPath } from '@/components/shell/menu-utils';
import { Card } from '@/components/shell/primitives';
import { api, ApiError } from '@/lib/api';
import type { Domain } from '@/lib/types';
import { useSessionStore } from '@/store/session';

const MODELO = 'sales.subscription';

/** `2026-08-01` a partir de un `Date`, sin pasar por la zona horaria del navegador. */
function iso(fecha: Date): string {
  return [
    fecha.getFullYear(),
    String(fecha.getMonth() + 1).padStart(2, '0'),
    String(fecha.getDate()).padStart(2, '0'),
  ].join('-');
}

interface Ventana {
  /** Clave estable para la caché; no se muestra. */
  clave: string;
  titulo: string;
  /** Se dibuja en grande. Sólo el mes en curso, que es la pregunta del dueño. */
  destacada: boolean;
  dominio: Domain;
}

/**
 * Las tres ventanas de tiempo, calculadas al vuelo.
 *
 * «Mes pasado» está para que el del mes en curso signifique algo: un número
 * suelto no dice si el mes viene bien o mal. «Este año» es el acumulado, que es
 * lo que se mira en una reunión.
 */
function ventanas(): Ventana[] {
  const hoy = new Date();
  const año = hoy.getFullYear();
  const mes = hoy.getMonth();

  const primeroDeEsteMes = new Date(año, mes, 1);
  const ultimoDeEsteMes = new Date(año, mes + 1, 0);
  const primeroDelPasado = new Date(año, mes - 1, 1);
  const ultimoDelPasado = new Date(año, mes, 0);

  return [
    {
      clave: 'mes',
      titulo: 'Este mes',
      destacada: true,
      dominio: [
        ['start_date', '>=', iso(primeroDeEsteMes)],
        ['start_date', '<=', iso(ultimoDeEsteMes)],
      ],
    },
    {
      clave: 'mes-pasado',
      titulo: 'Mes pasado',
      destacada: false,
      dominio: [
        ['start_date', '>=', iso(primeroDelPasado)],
        ['start_date', '<=', iso(ultimoDelPasado)],
      ],
    },
    {
      clave: 'año',
      titulo: 'En el año',
      destacada: false,
      dominio: [
        ['start_date', '>=', `${año}-01-01`],
        ['start_date', '<=', `${año}-12-31`],
      ],
    },
  ];
}

interface Medida {
  clave: string;
  titulo: string;
  destacada: boolean;
  dominio: Domain;
  total: string;
  cuantas: number;
}

async function medir(signal: AbortSignal): Promise<Medida[]> {
  // LAS TRES VENTANAS SE PIDEN JUNTAS, no una esperando a la anterior.
  //
  // Eran tres `await` adentro de un `for`, y las tres consultas son
  // independientes: ninguna usa el resultado de la anterior. Medido en el
  // navegador, salían encadenadas —384→621, 625→821, 830→853 ms—, o sea tres
  // viajes en fila. Contra producción, donde cada viaje cuesta unos 450 ms, eso
  // es más de un segundo de este panel esperándose a sí mismo.
  //
  // `limit: 1` a propósito: de estas consultas sólo interesan `count` y
  // `totals`, que el backend calcula sobre el filtro entero y no sobre la
  // página. Una fila y no cero porque un `limit: 0` significa «sin límite» en la
  // API genérica, que es justo lo contrario.
  //
  // Y AHORA EN UN SOLO PEDIDO (`/contar/`): el mismo número y el mismo total
  // que daría la lista con cada dominio, sin pagar tres pedidos ni hacer cola
  // en el servidor junto con el resto de la pantalla de inicio.
  const lista = ventanas();
  const resultados = await api.contar(
    lista.map((ventana) => ({ model: MODELO, domain: ventana.dominio, totales: true })),
    signal,
  );
  return lista.map((ventana, indice): Medida => {
    const respuesta = resultados[indice];
    // Igual que antes cuando la lista de una ventana fallaba: el panel entero
    // no se dibuja (ver `VentasDelMes`).
    if (respuesta === undefined || respuesta.error !== undefined) {
      throw new Error(respuesta?.detail ?? 'No se pudieron contar las ventas.');
    }
    const total = respuesta.totals?.amount;
    return {
      clave: ventana.clave,
      titulo: ventana.titulo,
      destacada: ventana.destacada,
      dominio: ventana.dominio,
      total: typeof total === 'string' || typeof total === 'number' ? String(total) : '0',
      cuantas: respuesta.count ?? 0,
    };
  });
}

export function VentasDelMes() {
  const companyId = useSessionStore((estado) => estado.activeCompanyId);
  const autenticado = useSessionStore((estado) => estado.status === 'authenticated');

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['ventas-del-mes', companyId],
    queryFn: ({ signal }) => medir(signal),
    enabled: autenticado,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Sin permiso de lectura sobre ventas, o sin el módulo instalado: el panel
  // desaparece en vez de mostrar un error técnico en lo primero que se ve.
  if (isError && error instanceof ApiError && (error.isPermissionError || error.status === 404)) {
    return null;
  }
  if (isError) return null;

  const medidas = data ?? [];
  const mes = medidas.find((una) => una.destacada);
  const vacio = medidas.length > 0 && medidas.every((una) => una.cuantas === 0);

  return (
    <section aria-labelledby="vendido-titulo" className="mt-8">
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <h2 id="vendido-titulo" className="text-sm font-semibold uppercase tracking-wide text-fg-faint">
          Lo vendido
        </h2>
        <Link
          to={modelPath(MODELO, 'sales.menu_subscription_all')}
          className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
        >
          Ver todas las ventas
          <IconChevronRight className="h-4 w-4" />
        </Link>
      </div>

      <Card className="p-5">
        {isPending ? (
          <p className="text-sm text-fg-muted">Calculando…</p>
        ) : (
          <>
            <div className="grid gap-5 sm:grid-cols-3">
              {medidas.map((medida) => (
                <Link
                  key={medida.clave}
                  to={filteredModelPath(MODELO, medida.dominio)}
                  className="group block rounded-lg px-1 py-0.5 transition-colors hover:bg-surface-raised"
                >
                  <p className="text-2xs font-semibold uppercase tracking-wide text-fg-faint">
                    {medida.titulo}
                  </p>
                  <p
                    className={
                      'mt-1 font-semibold tabular-nums ' +
                      (medida.destacada ? 'text-3xl text-fg' : 'text-xl text-fg-muted')
                    }
                  >
                    {formatCurrency(medida.total)}
                  </p>
                  <p className="mt-0.5 text-xs text-fg-faint">
                    {medida.cuantas === 0
                      ? 'sin ventas'
                      : `${medida.cuantas} ${medida.cuantas === 1 ? 'venta' : 'ventas'}`}
                  </p>
                </Link>
              ))}
            </div>

            <p className="mt-4 border-t border-line pt-3 text-xs text-fg-muted">
              {vacio ? (
                <>
                  Todavía no hay ventas cargadas. Apenas cargues la primera, el total de este mes se
                  actualiza solo.
                </>
              ) : (
                <>
                  Es lo <strong className="font-medium text-fg">vendido</strong>, por fecha de venta.
                  Facturado es otra cosa y se emite a mano: entrá a una venta para hacerlo. Tocá
                  cualquier número para ver qué ventas lo componen
                  {mes !== undefined && mes.cuantas > 0 ? ', o agrupá por cliente para ver quién compró más' : ''}
                  .
                </>
              )}
            </p>
          </>
        )}
      </Card>
    </section>
  );
}
